import {IconButton} from "@mui/material";
import {FiTrash2} from "react-icons/fi";
import {useNavigate} from "react-router-dom";
import {useCallback, useMemo} from "react";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import SearchBar from "../components/ui/SearchBar";
import {tokenColumns} from "../constants/dataGridColumns";
import {tokenSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useConfirmDialog from "../hooks/useConfirmDialog";
import usePaginationSearch from "../hooks/usePaginationSearch";
import LoadingContent from "../components/ui/LoadingContent";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";
import {snackbar} from "../utils/snackbar";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const Tokens = () => {
  const navigate = useNavigate();
  const {data, loading, list, remove} = useApiRequest({baseUrl: "/tokens", resource: "tokens"});
  const {can} = useAuthPermissions();
  const {list: listUsers} = useApiRequest({baseUrl: "/users", resource: "users"});

  const fetcher = useCallback(
    async (query) => {
      const {success, data} = await list(query);
      if (!success) return null;

      return {data};
    },
    [list]
  );

  const {filters, applyFilters, resetFilters, dataGridProps, refresh} = usePaginationSearch({
    fetcher,
    initialSortModel: [{field: "expires_at", sort: "desc"}]
  });

  // El backend exige userId como uuid, así que se resuelve por email antes de filtrar.
  const loadUsers = useCallback(
    async (term) => {
      const res = await listUsers(term ? {email: term} : undefined);
      return normalizeArray(res?.data).map((u) => ({id: u.id, label: u.email || u.id}));
    },
    [listUsers]
  );

  const fields = useMemo(
    () => [
      ...tokenSearchFields,
      {
        field: "userId",
        label: "Buscar usuario",
        type: "autocomplete",
        filterMode: "server",
        width: 250,
        loadOptions: loadUsers
      }
    ],
    [loadUsers]
  );

  const handleUserClick = (e, userId) => {
    e.stopPropagation();
    if (userId) {
      navigate(`/user-form`, {state: {id: userId}});
    }
  };

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Token eliminado correctamente.");
    refresh();
  });

  const handleOpenDeleteDialog = (id) => {
    const found = Array.isArray(data?.data) ? data.data.find((token) => token.id === id) : null;
    openDialog(id, found?.token || "");
  };

  const columns = [
    ...tokenColumns.filter((col) => col.field !== "email"),
    {
      field: "email",
      headerName: "Usuario",
      width: 180,
      editable: false,
      renderCell: (params) => (
        <span
          style={{
            color: "primary.main",
            textDecoration: "underline",
            cursor: "pointer"
          }}
          onClick={(e) => handleUserClick(e, params.row.user_id)}
        >
          {params.row.email}
        </span>
      )
    },
    ...(can(ACTIONS.DELETE, RESOURCES.TOKENS)
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteDialog(params.row.id);
                }}
              >
                <FiTrash2 />
              </IconButton>
            )
          }
        ]
      : [])
  ];

  return (
    <Window title={"Tokens"}>
      {loading ? (
        <LoadingContent />
      ) : (
        <>
          <SearchBar fields={fields} initialValues={filters} onSearch={applyFilters} onReset={resetFilters} />
          <ListView columns={columns} getRowId={(row) => row.id} loading={loading} dataGridProps={dataGridProps} />
        </>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="token"
        entityLabel={itemName}
      />
    </Window>
  );
};

export default Tokens;
